/* eslint-disable camelcase */
import Checkbox from 'common/components/Checkbox'
import Flex from 'common/components/Flex'
import Image from 'common/components/Image'
import Snackbar from 'common/components/Snackbar'
import Typography from 'common/components/Typography'
import theme from 'common/theme'
import { GAUniversalEvent } from 'lib/google-analytics'
import { useEffect, useState } from 'react'
import GAEvent from 'services/Questionnaire/event/google-analytics'
import styled from 'styled-components'

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Card = styled.div`
  width: 100%;
  padding: 16px;
  border: solid 1px
    ${(props) =>
      props.isActive
        ? theme.colors.orange
        : theme.colors.grayLighter};
  border-radius: ${theme.radius.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  background-color: ${(props) =>
    props.isActive
      ? theme.colors.orangeYellowLight
      : theme.colors.white};

  :hover {
    border: solid 1px ${theme.colors.orange};
  }
`

interface IChecklistCard {
  onChange: (id: string, value: Object) => void
  data: {
    id: string
    question: string
    answer_type: string
    options?: Array<{
      id: string
      option: string
      image_url: string
      description?: string
      next_questions?: {
        url: string
        method: string
      }
    }>
    validation_rules?: Array<{
      name: string
      value: any
    }>
  }
  defaultValue?: Array<string>
}

function ChecklistCard({
  onChange,
  data,
  defaultValue = [],
}: IChecklistCard): JSX.Element {
  const [value, setValue] = useState(defaultValue)
  const [isError, setIsError] = useState(false)
  const [errorWording, setErrorWording] = useState('')

  useEffect(() => {
    const maxLength = data?.validation_rules?.find(
      (vr) => vr.name === 'max_select',
    )
    if (maxLength?.value) {
      setErrorWording(
        `Anda hanya bisa memilih max ${maxLength.value} kategori`,
      )
    }
  }, [data])

  const handleChange = (val) => {
    let newValue = [...value]
    const maxLength = data?.validation_rules?.find(
      (vr) => vr.name === 'max_select',
    )

    const indexVal = value?.findIndex((v) => v === val.id)
    if (indexVal >= 0) {
      // Uncheck checked options
      newValue.splice(indexVal, 1)
    } else {
      if (GAEvent[data.question]) {
        GAUniversalEvent(
          GAEvent[data.question].category,
          GAEvent[data.question].action.replace('$var', val.option),
          GAEvent[data.question].label.replace('$var', val.option),
        )
      }
      newValue = [...value, val.id]
    }
    if (maxLength && maxLength?.value < newValue?.length) {
      setIsError(true)
    } else {
      setIsError(false)
      setValue(newValue)
      onChange(data?.id, newValue)
    }
  }

  const isActive = (id) => value?.findIndex((v) => v === id) >= 0

  return (
    <>
      <Snackbar
        open={isError}
        onClose={() => setIsError(false)}
        type="info"
        side="top"
        position="center"
      >
        {errorWording}
      </Snackbar>
      <CardContainer data-testid="checklist-card-wrapper-v2">
        {data?.options?.map((opt) => {
          const isChecked = isActive(opt.id)
          const handleClick = () => handleChange(opt)

          return (
            <Card
              key={opt.id}
              data-testid={`card-${opt.id}`}
              isActive={isChecked}
              onClick={handleClick}
            >
              <Flex flexDirection="column">
                <Flex gap="5px" alignItems="center">
                  <Image
                    lazy
                    src={opt.image_url}
                    height="18px"
                    width="18px"
                    objectFit="contain"
                  />
                  <Typography
                    type="bh6"
                    color={isChecked ? 'orange' : 'black'}
                  >
                    {opt.option}
                  </Typography>
                </Flex>
                {opt.description && (
                  <Typography color="grayDark" marginTop={10}>
                    {opt.description}
                  </Typography>
                )}
              </Flex>
              <Checkbox checked={isChecked} onClick={handleClick} />
            </Card>
          )
        })}
      </CardContainer>
    </>
  )
}

export default ChecklistCard
